import { CustomRoute } from "@/interfaces";
import { BarChart3, Briefcase, Building2, CheckSquare, Handshake, LayoutDashboard, ShieldCheck, Users } from "lucide-react";
import React from "react";
import { Navigate } from "react-router";
import { leadGenRoutes } from "./leadGen";

const Dashboard = React.lazy(() => import("@/modules/SuperAdmin/Dashboard"));
const UserManagement = React.lazy(() => import("@/modules/SuperAdmin/UserManagement"));
const CompanyManagement = React.lazy(() => import("@/modules/SuperAdmin/CompanyManagement"));
const BusinessUnitManagement = React.lazy(() => import("@/modules/SuperAdmin/BusinessUnitManagement"));
const BusinessVerticalManagement = React.lazy(() => import("@/modules/SuperAdmin/BusinessVerticalManagement"));
const ClientManagement = React.lazy(() => import("@/modules/SuperAdmin/ClientManagement"));
const VendorManagement = React.lazy(() => import("@/modules/SuperAdmin/VendorManagement"));
// const Organization = React.lazy(() => import("@/modules/SuperAdmin/OrganizationManagement")); // Deprecated
const ServiceManagement = React.lazy(() => import("@/modules/SuperAdmin/ServiceManagement"));
const AccessTokenManagement = React.lazy(() => import("@/modules/SuperAdmin/AccessTokenManagement"));
const BillApprovalConfigs = React.lazy(() => import("@/modules/SuperAdmin/BillApprovalConfigs"));
const CommissionApprovalConfigs = React.lazy(() => import("@/modules/SuperAdmin/CommissionApprovalConfigs"));
const ApprovalTemplates = React.lazy(() => import("@/modules/SuperAdmin/ApprovalTemplates"));
const ProjectsApprovals = React.lazy(() => import("@/modules/Admin/ProjectsApprovals"));
const EditProfile = React.lazy(() => import("@/modules/Profile/EditProfile"));
const Reports = React.lazy(() => import("@/modules/SuperAdmin/Reports"));
const FinancialReports = React.lazy(() => import("@/modules/SuperAdmin/Reports/FinancialReports"));
const OrderApprovalConfigs = React.lazy(() => import("@/modules/SuperAdmin/OrderApprovalConfigs/OrderApprovalConfigs"));
const DailyReportsPage = React.lazy(() => import("@/modules/Admin/OMS/DailyReports/DailyReportsPage"));
const PowerBiPage = React.lazy(() => import("@/modules/SuperAdmin/PowerBi"));
const PowerBiViewer = React.lazy(() => import("@/modules/SuperAdmin/PowerBi/PowerBiViewer"));
const DeploymentManagement = React.lazy(() => import("@/modules/SuperAdmin/DeploymentManagement"));
const DealsPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/index.tsx"));
const DealDetailsPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/DealDetailsPage.tsx"));
const DealPurchaseOrderPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/DealPurchaseOrderPage.tsx"));
const CrmCompaniesPage = React.lazy(() => import("@/modules/Admin/CRM/Companies/index.tsx"));
const CrmCompanyDetailsPage = React.lazy(() => import("@/modules/Admin/CRM/Companies/CompanyDetailsPage.tsx"));
const CrmMeetingsPage = React.lazy(() => import("@/modules/Admin/CRM/Meetings/index.tsx"));

// The CRM module's pages, in the shared CRM section after Lead Gen's lead pipeline (as for Admins and Employees).
const crmPages = (): CustomRoute[] => [
  // Contacts are a tab of CRM Companies; old links land on it
  { path: "/crm/contacts", id: "SuperAdmin", element: <Navigate to="/crm/companies?tab=contacts" replace />, hideInMenu: true, menuName: "CRM Companies", highlightOn: ["companies"] },
  { path: "/crm/companies", id: "SuperAdmin", element: <CrmCompaniesPage />, menuName: "CRM Companies", highlightOn: ["companies"] },
  { path: "/crm/companies/:id", id: "SuperAdmin", element: <CrmCompanyDetailsPage />, hideInMenu: true, menuName: "CRM Companies", highlightOn: ["companies"] },
  { path: "/crm/deals", id: "SuperAdmin", element: <DealsPage />, menuName: "Deals", highlightOn: ["deals"] },
  { path: "/crm/deals/:id", id: "SuperAdmin", element: <DealDetailsPage />, hideInMenu: true, menuName: "Deals", highlightOn: ["deals"] },
  { path: "/crm/deals/:id/po", id: "SuperAdmin", element: <DealPurchaseOrderPage />, hideInMenu: true, menuName: "Deals", highlightOn: ["deals"] },
  { path: "/crm/deals/:id/po/:poId", id: "SuperAdmin", element: <DealPurchaseOrderPage />, hideInMenu: true, menuName: "Deals", highlightOn: ["deals"] },
  { path: "/crm/meetings", id: "SuperAdmin", element: <CrmMeetingsPage />, menuName: "Meetings", highlightOn: ["meetings"] },
];

// The menu is grouped into sections (collapsible submenus); every page keeps its own path.
const superAdminRoutes: CustomRoute[] = [
  {
    path: "/",
    id: "SuperAdmin",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "Organization",
    icon: Building2,
    highlightOn: ["companies", "company", "business-units", "business-verticals", "users", "user"],
    submenu: [
      {
        path: "/companies",
        id: "SuperAdmin",
        element: <CompanyManagement />,
        menuName: "Companies",
        highlightOn: ["companies", "company"],
      },
      {
        path: "/companies/:id/edit/:tab",
        id: "SuperAdmin",
        element: <CompanyManagement />,
        menuName: "Edit Company",
        hideInMenu: true,
        highlightOn: ["companies", "company"],
      },
      {
        path: "/companies/:id/edit",
        id: "SuperAdmin",
        element: <CompanyManagement />,
        menuName: "Edit Company",
        hideInMenu: true,
        highlightOn: ["companies", "company"],
      },
      {
        path: "/business-units",
        id: "SuperAdmin",
        element: <BusinessUnitManagement />,
        menuName: "Business Units",
        highlightOn: ["business-units"],
      },
      // the one list of verticals: companies are mapped here, and Lead Gen leads use it
      {
        path: "/business-verticals",
        id: "SuperAdmin",
        element: <BusinessVerticalManagement />,
        menuName: "Business Verticals",
        highlightOn: ["business-verticals"],
      },
      {
        path: "/users",
        id: "SuperAdmin",
        element: <UserManagement />,
        menuName: "Users",
        highlightOn: ["users", "user"],
      },
    ],
  },
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "Clients & Vendors",
    icon: Handshake,
    highlightOn: ["clients", "client", "vendors", "vendor"],
    submenu: [
      {
        path: "/clients",
        id: "SuperAdmin",
        element: <ClientManagement />,
        menuName: "Clients",
        highlightOn: ["clients", "client"],
      },
      {
        path: "/vendors",
        id: "SuperAdmin",
        element: <VendorManagement />,
        menuName: "Vendors",
        highlightOn: ["vendors", "vendor"],
      },
    ],
  },
  // Organizations - Deprecated in favor of Client/Vendor first-class entities
  // {
  //   path: "/organizations",
  //   id: "SuperAdmin",
  //   element: <Organization />,
  //   menuName: "Organizations",
  //   icon: Building,
  //   highlightOn: ["organizations", "organization"],
  // },
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "Projects & Services",
    icon: Briefcase,
    highlightOn: ["projects-approvals", "services", "service", "approval-templates"],
    submenu: [
      {
        path: "/projects-approvals",
        id: "SuperAdmin",
        element: <ProjectsApprovals />,
        menuName: "Project Tasks",
        highlightOn: ["projects-approvals"],
      },
      {
        path: "/approval-templates",
        id: "SuperAdmin",
        element: <ApprovalTemplates />,
        menuName: "Project Templates",
        highlightOn: ["approval-templates"],
      },
      {
        path: "/services",
        id: "SuperAdmin",
        element: <ServiceManagement />,
        menuName: "Services",
        highlightOn: ["services", "service"],
      },
    ],
  },
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "Approvers",
    icon: CheckSquare,
    highlightOn: ["bill-approvals", "commission-approvals", "order-approvals"],
    submenu: [
      {
        path: "/bill-approvals",
        id: "SuperAdmin",
        element: <BillApprovalConfigs />,
        menuName: "Bill Approvers",
        highlightOn: ["bill-approvals"],
      },
      {
        path: "/commission-approvals",
        id: "SuperAdmin",
        element: <CommissionApprovalConfigs />,
        menuName: "Commission Approvers",
        highlightOn: ["commission-approvals"],
      },
      {
        path: "/order-approvals",
        id: "SuperAdmin",
        element: <OrderApprovalConfigs />,
        menuName: "Order Approvers",
        highlightOn: ["order-approvals"],
      },
    ],
  },
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "Reports",
    icon: BarChart3,
    highlightOn: ["reports", "daily-reports", "powerbi"],
    submenu: [
      {
        path: "/reports/general",
        id: "SuperAdmin",
        element: <Reports />,
        menuName: "General",
        highlightOn: ["reports-general"],
      },
      {
        path: "/reports/financials",
        id: "SuperAdmin",
        element: <FinancialReports />,
        menuName: "Financials",
        highlightOn: ["reports-financials"],
      },
      {
        path: "/oms/consumption-report",
        id: "SuperAdmin",
        element: <DailyReportsPage />,
        menuName: "Consumption Report",
        highlightOn: ["daily-reports"],
      },
      {
        path: "/powerbi",
        id: "SuperAdmin",
        element: <PowerBiPage />,
        menuName: "Power BI",
        highlightOn: ["powerbi"],
      },
      {
        path: "/powerbi/view/:id",
        id: "SuperAdmin",
        element: <PowerBiViewer />,
        menuName: "Power BI Viewer",
        hideInMenu: true,
        highlightOn: ["powerbi"],
      },
    ],
  },
  leadGenRoutes("SuperAdmin", { collector: true, crm: crmPages() }),
  {
    path: "",
    id: "SuperAdmin",
    element: null,
    menuName: "System",
    icon: ShieldCheck,
    highlightOn: ["access-tokens", "tokens", "deployments"],
    submenu: [
      {
        path: "/access-tokens",
        id: "SuperAdmin",
        element: <AccessTokenManagement />,
        menuName: "API Access Tokens",
        highlightOn: ["access-tokens", "tokens"],
      },
      {
        path: "/deployments",
        id: "SuperAdmin",
        element: <DeploymentManagement />,
        menuName: "Deployments",
        highlightOn: ["deployments"],
      },
    ],
  },
  {
    path: "/profile",
    id: "SuperAdmin",
    element: <EditProfile />,
    menuName: "Profile",
    icon: Users,
    highlightOn: ["profile"],
  },
];

export default superAdminRoutes;
